import { RadarChart } from '@mantine/charts'
import {
  Accordion,
  Badge,
  Box,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Divider,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { CheckCircle2, ListChecks } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { EvalMasteryResult } from '../../api/evaluations'
import { stripLeadingQuestionNumber } from '../../utils/questionText'

const MASTERY_LIST_ACCORDION_VALUE = 'mastery-list'
const RADAR_LABEL_MAX_LEN = 15

function formatRadarLabel(label: string) {
  return label.length > RADAR_LABEL_MAX_LEN ? `${label.slice(0, RADAR_LABEL_MAX_LEN)}...` : label
}

type MasteryRadarDatum = { label: string; value: number }

/* ------------------------------------------------------------------ */
/*  Mantine / Recharts radar (global max = outer ring)                  */
/* ------------------------------------------------------------------ */
function MasteryRadarChart({
  data,
  height,
  clampLabels = true,
}: {
  data: MasteryRadarDatum[]
  height: number
  /** When false (e.g. modal), axis shows full mastery names */
  clampLabels?: boolean
}) {
  const valueMax = useMemo(() => Math.max(...data.map((d) => d.value), 1), [data])
  const chartData = useMemo(
    () =>
      data.map((d) => ({
        mastery: clampLabels ? formatRadarLabel(d.label) : d.label,
        masteryFull: d.label,
        correct: d.value,
      })),
    [data, clampLabels],
  )

  return (
    <RadarChart
      h={height}
      w="100%"
      miw={0}
      data={chartData}
      dataKey="mastery"
      series={[
        {
          name: 'correct',
          color: 'grapefruit.6',
          strokeColor: 'grapefruit.7',
          opacity: 0.22,
          label: 'Correct',
        },
      ]}
      gridColor="dark.4"
      textColor="dimmed"
      withPolarGrid
      withPolarAngleAxis
      withPolarRadiusAxis
      withTooltip
      polarAngleAxisProps={{
        tick: { fontSize: clampLabels ? 10 : 11 },
      }}
      polarRadiusAxisProps={{
        domain: [0, valueMax],
      }}
      tooltipProps={{
        formatter: (value: number) => [value, 'Correct'],
        labelFormatter: (_label, payload) => {
          const row = payload?.[0]?.payload as { masteryFull?: string; mastery?: string } | undefined
          return row?.masteryFull ?? row?.mastery ?? ''
        },
      }}
    />
  )
}

/* ------------------------------------------------------------------ */
/*  EvaluateResult Component                                            */
/* ------------------------------------------------------------------ */
interface EvaluateResultProps {
  title?: string
  rwScore: number
  mathScore: number
  totalScore: number
  totalQuestions: number
  masteryResults: EvalMasteryResult[]
  isPractice?: boolean
}

export function EvaluateResult({ title, rwScore, mathScore, totalScore, totalQuestions, masteryResults, isPractice }: EvaluateResultProps) {
  const [masteryAccordion, setMasteryAccordion] = useState<string | null>(null)
  const [radarModalOpen, setRadarModalOpen] = useState(false)
  const masteryListOpen = masteryAccordion === MASTERY_LIST_ACCORDION_VALUE
  const displayTitle = stripLeadingQuestionNumber(title)

  const radarData = masteryResults
    .filter((m) => m.correct_count + m.wrong_count > 0)
    .map((m) => ({
      label: m.mastery_name,
      value: m.correct_count,
    }))

  return (
    <Stack gap="lg">
      {/* Score summary */}
      <Paper radius="xl"  withBorder style={{ background: 'var(--sat-surface)', textAlign: 'center' }}>
         {displayTitle && <Title order={2} c="var(--sat-text)" mb="sm">{displayTitle}</Title>}
        {isPractice ? (
          <Paper radius="xl" p="lg" withBorder style={{ background: 'var(--sat-surface-dim)', maxWidth: 260, margin: '16px auto 24px' }}>
            <Text size="xs" c="var(--sat-text-muted)" mb={4}>Score  </Text>
            <Text fz={48} fw={800} c="var(--sat-red)">{totalScore}</Text>
            <Text size="xs" c="var(--sat-text-muted)">out of {totalQuestions} questions</Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="xl" mt="lg">
            {[
              { label: 'Reading & Writing', value: rwScore, color: 'var(--sat-text)', helper: 'SAT section score' },
              { label: 'Math', value: mathScore, color: 'var(--sat-text)', helper: 'SAT section score' },
              { label: 'Total', value: totalScore, color: 'var(--sat-red)', helper: 'Combined SAT score' },
            ].map((s) => (
              <Paper key={s.label} radius="xl" p="lg" withBorder style={{ background: 'var(--sat-surface-dim)' }}>
                <Text size="xs" c="var(--sat-text-muted)" mb={4}>{s.label}</Text>
                <Text fz={36} fw={800} c={s.color}>{s.value}  </Text>
                <Text size="xs" c="var(--sat-text-muted)">{s.helper}</Text>
              </Paper>
            ))}
          </SimpleGrid>
        )}
      </Paper>

      {/* Mastery breakdown */}
      {masteryResults.length > 0 && (
        <Paper radius="xl" p="xl" withBorder style={{ background: 'var(--sat-surface)' }}>
          <Title order={3} c="var(--sat-text)" mb="md">Mastery Breakdown</Title>
          {!isPractice && radarData.length >= 3 && (
            <>
              <Tooltip label="Click to enlarge" position="top" withArrow>
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => setRadarModalOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setRadarModalOpen(true)
                    }
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: 24,
                    cursor: 'pointer',
                    borderRadius: 12,
                  }}
                >
                  <MasteryRadarChart data={radarData} height={440} />
                </Box>
              </Tooltip>
              <Modal
                opened={radarModalOpen}
                onClose={() => setRadarModalOpen(false)}
                title="Mastery radar"
                centered
                size="100%"
                padding="lg"
                styles={{
                  content: { maxWidth: 'min(960px, calc(100vw - 2rem))' },
                  body: { paddingTop: '0.5rem' },
                }}
              >
                <Box w="100%">
                  <MasteryRadarChart data={radarData} height={520} clampLabels={false} />
                </Box>
              </Modal>
            </>
          )}
          <Accordion
            value={masteryAccordion}
            onChange={setMasteryAccordion}
            chevronPosition="right"
          >
            <Accordion.Item value={MASTERY_LIST_ACCORDION_VALUE}>
              <Accordion.Control
                icon={
                  <Tooltip
                    label={masteryListOpen ? 'Click to close' : 'Click to Open'}
                    withArrow
                    position="right"
                  >
                    <Box component="span" style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <ListChecks size={18} strokeWidth={2} color="var(--sat-text-muted, #aaa)" />
                    </Box>
                  </Tooltip>
                }
              >
                <Divider mb="md" size="sm" />
                <Text size="sm" fw={600} c="var(--sat-text)">
                  Mastery list
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="xs" pt="xs">
                  {masteryResults.map((m) => {
                    const total = m.correct_count + m.wrong_count
                    const pct = total > 0 ? Math.round((m.correct_count / total) * 100) : 0
                    return (
                      <Paper key={m.mastery_id} p="sm" radius="md" style={{ background: 'var(--sat-surface-dim)' }}>
                        <Group justify="space-between" wrap="nowrap">
                          <Text size="sm" fw={500} c="var(--sat-text)" style={{ flex: 1, minWidth: 0 }} truncate>
                            {m.mastery_name}
                          </Text>
                          <Group gap={8} wrap="nowrap">
                            <Badge color="green" variant="light" size="sm">{m.correct_count} correct</Badge>
                            <Badge color="red" variant="light" size="sm">{m.wrong_count} wrong</Badge>
                            <Text size="xs" fw={700} c="var(--sat-text)" style={{ minWidth: 36, textAlign: 'right' }}>
                              {pct}%
                            </Text>
                          </Group>
                        </Group>
                      </Paper>
                    )
                  })}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Paper>
      )}
    </Stack>
  )
}
